import { ref, set, get, update, remove } from 'firebase/database';
import { database } from './firebase';
import { UserProfile } from '../types/user';
import { CartItem } from '../types/cart';
import { Order } from '../types/order';

const ensureDatabase = () => {
  if (!database) {
    console.warn('[Database] Firebase database is not configured.');
    return null;
  }
  return database;
};

// User Profile Operations
export const createUserProfile = async (
  userId: string,
  email: string,
  name: string
): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const userRef = ref(db, `users/${userId}/profile`);
  await set(userRef, {
    email,
    name,
    phone: null,
    shippingAddress: null,
    memberSince: new Date().toISOString(),
    cartItems: [],
  });
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  const db = ensureDatabase();
  if (!db) return null;
  const userRef = ref(db, `users/${userId}/profile`);
  const snapshot = await get(userRef);

  if (snapshot.exists()) {
    const profile = snapshot.val() as UserProfile;
    console.log('[Database] getUserProfile returned:', {
      userId,
      profile,
      shippingAddress: profile.shippingAddress,
      hasCoordinates: !!(profile.shippingAddress?.latitude && profile.shippingAddress?.longitude),
    });
    return profile;
  }
  console.log('[Database] getUserProfile: No profile found for userId:', userId);
  return null;
};

export const updateUserProfile = async (
  userId: string,
  data: Partial<UserProfile>
): Promise<void> => {
  console.log('[Database] updateUserProfile called with:', {
    userId,
    data,
    shippingAddress: data.shippingAddress,
  });

  const db = ensureDatabase();
  if (!db) return;
  const userRef = ref(db, `users/${userId}/profile`);
  await update(userRef, data);

  console.log('[Database] Profile updated successfully');
};

export const checkProfileComplete = async (userId: string): Promise<boolean> => {
  const profile = await getUserProfile(userId);
  return !!(profile?.shippingAddress && profile.shippingAddress.street && profile.shippingAddress.city);
};

// Helper function to remove undefined values (Firebase doesn't allow them)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    });
    return cleaned;
  }
  return obj;
};

// Cart Operations
export const saveCartToFirebase = async (
  userId: string,
  cartItems: CartItem[]
): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const cartRef = ref(db, `users/${userId}/profile/cartItems`);
  // Remove undefined values before saving
  const cleanedItems = removeUndefined(cartItems);
  console.log('[Database] Saving cart with cleaned items:', cleanedItems);
  await set(cartRef, cleanedItems);
};

export const loadCartFromFirebase = async (userId: string): Promise<CartItem[]> => {
  const db = ensureDatabase();
  if (!db) return [];
  const cartRef = ref(db, `users/${userId}/profile/cartItems`);
  const snapshot = await get(cartRef);

  if (snapshot.exists()) {
    return snapshot.val() as CartItem[];
  }
  return [];
};

export const clearCart = async (userId: string): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const cartRef = ref(db, `users/${userId}/profile/cartItems`);
  await set(cartRef, []);
};

// Order Operations
export const createOrder = async (userId: string, order: Order): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const orderRef = ref(db, `users/${userId}/orders/${order.id}`);
  const orderData = {
    ...order,
    orderDate: order.orderDate.toISOString(),
  };
  // Remove undefined values before saving
  const cleanedOrder = removeUndefined(orderData);
  console.log('[Database] Creating order with cleaned data:', cleanedOrder);
  await set(orderRef, cleanedOrder);
};

export const getUserOrders = async (userId: string): Promise<Order[]> => {
  const db = ensureDatabase();
  if (!db) return [];
  const ordersRef = ref(db, `users/${userId}/orders`);
  const snapshot = await get(ordersRef);

  if (snapshot.exists()) {
    const ordersData = snapshot.val();
    return Object.values(ordersData).map((order: any) => ({
      ...order,
      orderDate: new Date(order.orderDate),
    }));
  }
  return [];
};

export const updateOrderStatus = async (
  userId: string,
  orderId: string,
  status: string
): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const orderRef = ref(db, `users/${userId}/orders/${orderId}/status`);
  await set(orderRef, status);
};

export const deleteUserData = async (userId: string): Promise<void> => {
  const db = ensureDatabase();
  if (!db) return;
  const userRef = ref(db, `users/${userId}`);
  await remove(userRef);
};
